import type { DentalAsset } from './types';
import { apiErrorText } from './locale';

const AUTH_KEY = 'dental-cloud-auth';
interface Auth { token: string; user: { id: string; login: string } }
export function currentAuth(): Auth | null {
  try { return JSON.parse(sessionStorage.getItem(AUTH_KEY) || 'null'); }
  catch { return null; }
}
export function logout() { sessionStorage.removeItem(AUTH_KEY); window.location.assign('/app/login'); }
export class ApiError extends Error {
  constructor(public status: number, public detail: string) { super(apiErrorText(detail, status)); }
}
async function responseFor(path: string, options: RequestInit = {}) {
  const headers = new Headers(options.headers);
  const auth = currentAuth();
  if (auth) headers.set('Girder-Token', auth.token);
  const response = await fetch(`/api/v1/${path}`, { cache: 'no-store', ...options, headers, credentials: 'same-origin' });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new ApiError(response.status, body.message || `Request failed (${response.status}).`);
  }
  return response;
}
export async function api<T>(path: string, method = 'GET', body?: unknown, options: RequestInit = {}): Promise<T> {
  return (await responseFor(path, { ...options, method, ...(body === undefined ? {} : {
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  }) })).json();
}
export async function authenticate(login: string, password: string) {
  const bytes = new TextEncoder().encode(`${login}:${password}`);
  const result = await (await responseFor('user/authentication', {
    headers: { Authorization: `Basic ${btoa(String.fromCharCode(...bytes))}` },
  })).json();
  sessionStorage.setItem(AUTH_KEY, JSON.stringify({ token: result.authToken.token,
    user: { id: result.user._id, login: result.user.login } }));
}
export type TransferProgress = { received: number; total: number | null };
export type ProgressCallback = (progress: TransferProgress) => void;
export async function responseBlob(response: Response, expectedSize?: number, progress?: ProgressCallback): Promise<Blob> {
  if (!progress) return response.blob();
  // Content-Length is compressed when HTTP encoding is used; the reader yields decoded bytes.
  const length = response.headers.get('Content-Encoding') ? 0 : Number(response.headers.get('Content-Length'));
  const total = length > 0 ? length : expectedSize && expectedSize > 0 ? expectedSize : null;
  progress({ received: 0, total });
  if (!response.body) {
    const blob = await response.blob(); progress({ received: blob.size, total: blob.size }); return blob;
  }
  const reader = response.body.getReader(); const chunks: BlobPart[] = []; let received = 0; let updatedAt = 0;
  try {
    while (true) {
      const { done, value } = await reader.read(); if (done) break;
      chunks.push(value as Uint8Array<ArrayBuffer>); received += value.byteLength;
      if (performance.now() - updatedAt >= 100) { progress({ received, total }); updatedAt = performance.now(); }
    }
  } finally { reader.releaseLock(); }
  progress({ received, total: received });
  return new Blob(chunks, { type: response.headers.get('Content-Type') || 'application/octet-stream' });
}
export async function assetFile(asset: DentalAsset, progress?: ProgressCallback): Promise<File> {
  const path = `file/${asset.fileId}/download`;
  // Avoid a single long transfer for large CBCTs; each bounded range is checked
  // before advancing, and an interrupted range can be retried independently.
  const blob = asset.byteSize >= 64 * 1024 * 1024 ? await downloadRangedBlob(path,asset.byteSize,progress) :
    await responseBlob(await responseFor(path),asset.byteSize,progress);
  return new File([blob], asset.name, { type: 'application/octet-stream' });
}
export async function downloadRangedBlob(path: string, size: number, progress?: ProgressCallback, chunkBytes = 8*1024*1024): Promise<Blob> {
  if (!Number.isSafeInteger(size) || size < 0 || !Number.isSafeInteger(chunkBytes) || chunkBytes <= 0) throw new Error('Invalid image download size.');
  const chunks: Blob[] = []; progress?.({received:0,total:size});
  for (let start=0;start<size;start+=chunkBytes) {
    const end=Math.min(size-1,start+chunkBytes-1); let complete=false;
    for (let attempt=0;attempt<3;attempt++) {
      try {
        const response=await responseFor(path,{headers:{Range:`bytes=${start}-${end}`}});
        if (response.status===200 && start===0) {
          const blob=await responseBlob(response,size,progress);
          if (blob.size!==size) throw new Error('Image download was interrupted.');
          return blob;
        }
        const range=/^bytes (\d+)-(\d+)\/(\d+)$/.exec(response.headers.get('Content-Range') || '');
        if (response.status!==206 || !range || +range[1]!==start || +range[2]!==end || +range[3]!==size) {
          await response.body?.cancel(); throw new Error('Image download returned an inconsistent byte range.');
        }
        const blob=await responseBlob(response,end-start+1,update=>progress?.({received:start+update.received,total:size}));
        if (blob.size!==end-start+1) throw new Error('Image download was interrupted before this range completed.');
        chunks.push(blob); complete=true; break;
      } catch(error) {
        if (error instanceof ApiError && error.status>=400 && error.status<500) throw error;
        if (attempt===2) throw new Error(`影像下载中断（${Math.floor(start/1024/1024)} MB处）。请刷新重试。`);
        await new Promise<void>(resolve=>setTimeout(resolve,250*(attempt+1)));
      }
    }
    if (!complete) throw new Error('Image range did not complete.');
  }
  progress?.({received:size,total:size}); return new Blob(chunks,{type:'application/octet-stream'});
}
export async function assetFiles(asset: DentalAsset, progress?: ProgressCallback): Promise<File[]> {
  if (asset.format !== 'dicom-series') return [await assetFile(asset, progress)];
  if (!asset.dicomInstances?.length) throw new Error('The DICOM series has no registered instances.');
  const files: File[] = [];
  let received = 0;
  // Bound simultaneous transfers/memory; the loader categorizes the full series.
  for (const row of asset.dicomInstances) {
    const response = await responseFor(`file/${row.fileId}/download`);
    const blob = await responseBlob(response, undefined, progress ? update => progress({ received: received + update.received, total: asset.byteSize || null }) : undefined);
    files.push(new File([blob], row.name, { type: 'application/dicom' })); received += blob.size;
  }
  progress?.({ received, total: received });
  return files;
}
export async function uploadFile(folderId: string, file: File, progress?: ProgressCallback): Promise<string> {
  const query = new URLSearchParams({ parentType: 'folder', parentId: folderId, name: file.name,
    size: String(file.size), mimeType: file.type || 'application/octet-stream' });
  progress?.({ received: 0, total: file.size });
  let upload = await (await responseFor(`file?${query}`, { method: 'POST' })).json();
  if (upload.itemId) { progress?.({ received: file.size, total: file.size }); return upload._id; }
  let offset = Number(upload.received || 0);
  while (offset < file.size) {
    const block = file.slice(offset, Math.min(file.size, offset + 8 * 1024 * 1024));
    const params = new URLSearchParams({ uploadId: upload._id, offset: String(offset) });
    const next = await (await responseFor(`file/chunk?${params}`, {
      method: 'POST', headers: { 'Content-Type': 'application/octet-stream' }, body: block,
    })).json();
    offset += block.size;
    if (next.itemId) { progress?.({ received: offset, total: file.size }); return next._id; }
    if (Number(next.received) !== offset) throw new Error('Upload offset was not confirmed. Retry before saving.');
    progress?.({ received: offset, total: file.size });
    upload = next;
  }
  throw new Error('Upload was not finalized; no annotation revision was saved.');
}
/** Verify only the newly uploaded annotation bytes, never the source CBCT. */
export async function verifyAnnotationFile(asset: DentalAsset, expected: File, progress?: ProgressCallback) {
  if (!['labelmap', 'meshLabels'].includes(asset.kind) || asset.byteSize !== expected.size) throw new Error('服务器标注文件信息与本次上传不一致。');
  const response = await responseFor(`file/${asset.fileId}/download`, { signal: AbortSignal.timeout(120000) });
  let offset = 0; progress?.({ received: 0, total: expected.size });
  async function compare(bytes: Uint8Array) {
    if (offset + bytes.length > expected.size) throw new Error('服务器标注文件长度与本次上传不一致。');
    const original = new Uint8Array(await expected.slice(offset, offset + bytes.length).arrayBuffer());
    if (bytes.some((value, i) => value !== original[i])) throw new Error('服务器标注文件内容与本次上传不一致。');
    offset += bytes.length; progress?.({ received: offset, total: expected.size });
  }
  if (response.body) {
    const reader = response.body.getReader();
    try { while (true) { const { done, value } = await reader.read(); if (done) break; await compare(value); } }
    finally { await reader.cancel().catch(() => {}); reader.releaseLock(); }
  } else await compare(new Uint8Array(await response.arrayBuffer()));
  if (offset !== expected.size) throw new Error('服务器标注文件回读不完整，请重试确认。');
}
export function downloadBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob); const link = document.createElement('a');
  link.href = url; link.download = name; document.body.appendChild(link); link.click(); link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
